import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LoadingSkeleton,
  FormControl,
  HairlineCard,
  Icons,
  iconSize,
  InputLabel,
  MenuItem,
  palette,
  Select,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useFiles } from '../lib/files-api';
import { formatDateTime } from '../lib/relative-time';
import { useEvidence, useUploadEvidence } from '../lib/evidence-api';
import { useScope } from '../providers/ScopeProvider';

interface Pending {
  photo: File;
  previewUrl: string;
  latitude: number | null;
  longitude: number | null;
  capturedAt: string;
}

/** Read the device GPS once, resolving to null coords if denied/unavailable. */
function readPosition(): Promise<{ latitude: number | null; longitude: number | null }> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve({ latitude: null, longitude: null });
      return;
    }
    // Geotagging the evidence is the core purpose of this screen; a denied fix degrades to null coords.

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({ latitude: null, longitude: null }),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  });
}

function formatCoords(lat: number | null, lng: number | null, noLocationLabel: string) {
  if (lat == null || lng == null) return noLocationLabel;
  const ns = `${Math.abs(lat).toFixed(4)}°${lat >= 0 ? 'N' : 'S'}`;
  const ew = `${Math.abs(lng).toFixed(4)}°${lng >= 0 ? 'E' : 'W'}`;
  return `${ns}, ${ew}`;
}

function formatTime(iso: string, language: string): string {
  return formatDateTime(iso, language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Capture() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Same gate as the dashboard's "New file": `file:create` comes with an
  // appointment, so an unappointed contractor is not offered a dead end.
  const { can } = useScope();
  const canCreateFile = can('file:create');
  const { data, isLoading: filesLoading } = useFiles();
  const files = useMemo(() => (data?.items ?? []).filter((f) => f.status !== 'closed'), [data]);

  const [fileId, setFileId] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useUploadEvidence(fileId || undefined);
  const evidence = useEvidence(fileId || undefined);

  // Default the attach-to-file selector to the first open file.
  useEffect(() => {
    if (!fileId && files.length > 0) setFileId(files[0].id);
  }, [files, fileId]);

  // Revoke the object URL when the pending photo changes or unmounts.
  useEffect(() => {
    return () => {
      if (pending) URL.revokeObjectURL(pending.previewUrl);
    };
  }, [pending]);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const photo = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!photo) return;
    setDone(false);
    const coords = await readPosition();
    setPending({
      photo,
      previewUrl: URL.createObjectURL(photo),
      latitude: coords.latitude,
      longitude: coords.longitude,
      capturedAt: new Date().toISOString(),
    });
  };

  const discard = () => {
    if (pending) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
    setNote('');
  };

  const attach = () => {
    if (!pending || !fileId) return;
    upload.mutate(
      {
        photo: pending.photo,
        fields: {
          latitude: pending.latitude,
          longitude: pending.longitude,
          capturedAt: pending.capturedAt,
          note,
        },
      },
      {
        onSuccess: () => {
          discard();
          setDone(true);
        },
      },
    );
  };

  const list = evidence.data ?? [];

  return (
    <AppShell>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: { md: 'none' }, letterSpacing: '-0.01em' }}>
          {t('gov.capture.title', { defaultValue: 'Capture evidence' })}
        </Typography>
        {/* The stamping promise only means something next to a camera; on the
            empty state there is nothing to stamp yet. */}
        {!filesLoading && files.length > 0 && (
          <Typography sx={{ mt: 0.5, color: palette.text.secondary }}>
            {t('gov.capture.subtitle')}
          </Typography>
        )}
      </Box>

      {filesLoading && <LoadingSkeleton variant="form" label={t('common.labels.loading')} />}

      {!filesLoading && files.length === 0 && (
        <HairlineCard
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.brand.accentTint,
            }}
          >
            <Icons.Camera size={iconSize.xl} color={palette.brand.accent} />
          </Box>
          <Typography variant="h6" component="h2">
            {t('gov.capture.noFilesTitle')}
          </Typography>
          <Typography sx={{ color: palette.text.secondary, maxWidth: 380 }}>
            {canCreateFile ? t('gov.capture.noFiles') : t('gov.capture.noFilesNoAuthority')}
          </Typography>
          {canCreateFile && (
            <Button
              variant="contained"
              startIcon={<Icons.Plus size={iconSize.lg} />}
              onClick={() => navigate('/files/new')}
            >
              {t('common.actions.newFile')}
            </Button>
          )}
        </HairlineCard>
      )}

      {!filesLoading && files.length > 0 && (
        <Stack spacing={2.5}>
          {done && (
            <Alert severity="success" onClose={() => setDone(false)}>
              {t('gov.capture.attached', { defaultValue: 'Photo captured and attached.' })}
            </Alert>
          )}

          {/* Attach-to-file selector */}
          <FormControl fullWidth size="small">
            <InputLabel id="capture-attach-to-label">
              {t('gov.capture.attachTo', { defaultValue: 'Attach to file' })}
            </InputLabel>
            <Select
              labelId="capture-attach-to-label"
              label={t('gov.capture.attachTo', { defaultValue: 'Attach to file' })}
              aria-label={t('gov.capture.attachTo', { defaultValue: 'Attach to file' })}
              value={fileId}
              onChange={(e) => setFileId(e.target.value)}
            >
              {files.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.reference} · {f.address}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Hidden camera input; capture="environment" opens the rear camera on mobile. */}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={onPick}
          />

          {!pending ? (
            <HairlineCard
              sx={{
                py: 6,
                px: 3,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: palette.brand.accentTint,
                }}
              >
                <Icons.Camera size={iconSize.xl} color={palette.brand.accent} />
              </Box>
              <Typography sx={{ color: palette.text.secondary, maxWidth: 360 }}>
                {t('gov.capture.prompt', {
                  defaultValue:
                    'Photograph the violation. Location and time are stamped automatically.',
                })}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icons.Camera size={iconSize.lg} />}
                onClick={() => inputRef.current?.click()}
              >
                {t('gov.capture.takePhoto', { defaultValue: 'Take photo' })}
              </Button>
            </HairlineCard>
          ) : (
            <HairlineCard sx={{ overflow: 'hidden' }}>
              <Box sx={{ position: 'relative' }}>
                {/*
                  The captured evidence photo is the subject of this screen, not
                  decoration — an empty `alt` hid it entirely (WCAG 1.1.1). The
                  geotag/timestamp strip below is the only description available
                  for a photo that has not been annotated yet, so use it.
                */}
                <Box
                  component="img"
                  src={pending.previewUrl}
                  alt={t('gov.capture.pendingPhotoAlt', {
                    time: formatTime(pending.capturedAt, i18n.language),
                  })}
                  sx={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block' }}
                />
                {/* Geotag / timestamp strip */}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    px: 1.5,
                    py: 1,
                    backgroundColor: palette.overlay.railSticky,
                  }}
                >
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Icons.MapPin size={iconSize.sm} color={palette.text.onDarkSubtle} />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: palette.text.onDark,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatCoords(
                        pending.latitude,
                        pending.longitude,
                        t('gov.capture.noLocation', { defaultValue: 'Location unavailable' }),
                      )}
                    </Typography>
                  </Stack>
                  <Box sx={{ flexGrow: 1 }} />
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Icons.Clock size={iconSize.sm} color={palette.text.onDarkSubtle} />
                    <Typography sx={{ fontSize: 12, color: palette.text.onDark }}>
                      {formatTime(pending.capturedAt, i18n.language)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('gov.capture.notePlaceholder', {
                    defaultValue: 'Add a note (optional)',
                  })}
                  // The placeholder was the only label, and it disappears as
                  // soon as the officer types (WCAG 1.3.1 / 4.1.2).
                  aria-label={t('gov.capture.notePlaceholder', {
                    defaultValue: 'Add a note (optional)',
                  })}
                />
                {upload.isError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {t('gov.capture.uploadError', {
                      defaultValue: 'Upload failed. Check your connection and try again.',
                    })}
                  </Alert>
                )}
                <Stack direction="row" spacing={1.5} sx={{ mt: 2, justifyContent: 'flex-end' }}>
                  <Button variant="text" onClick={discard} disabled={upload.isPending}>
                    {t('common.actions.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={attach}
                    disabled={upload.isPending}
                    startIcon={
                      upload.isPending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Icons.Check size={iconSize.lg} />
                      )
                    }
                  >
                    {t('gov.capture.attach', { defaultValue: 'Attach to file' })}
                  </Button>
                </Stack>
              </Box>
            </HairlineCard>
          )}

          {/* Recent captures on this file */}
          <Box>
            <Typography
              sx={{ mb: 1.5, fontSize: 13, fontWeight: 700, color: palette.text.secondary }}
            >
              {t('gov.capture.recent', { defaultValue: 'Recent on this file' })}
            </Typography>
            {evidence.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={22} />
              </Box>
            ) : list.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: palette.text.muted }}>
                {t('gov.capture.recentEmpty', { defaultValue: 'No evidence captured yet.' })}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: 1,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))',
                }}
              >
                {list.map((e) => (
                  <Box
                    key={e.id}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1 / 1',
                      borderRadius: 1.5,
                      overflow: 'hidden',
                      border: `1px solid ${palette.border.line}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={e.downloadUrl}
                      // Falling back to `''` left every un-annotated capture in
                      // the grid unlabelled.
                      alt={e.note?.trim() || t('gov.capture.captureAlt')}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        px: 0.75,
                        py: 0.25,
                        fontSize: 10,
                        color: palette.text.onDark,
                        backgroundColor: palette.overlay.railSticky,
                      }}
                    >
                      {formatTime(e.capturedAt, i18n.language)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Stack>
      )}
    </AppShell>
  );
}
